import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest } from "@/auth/authConfig";
import { usePermissions } from "@/contexts/PermissionContext";
import { InstallPromptProvider } from "@/contexts/InstallPromptContext";
import { usersService } from "@/services/usersService";
import { useEffect, useRef } from "react";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Dashboard from "@/pages/Dashboard";
import EstimateForm from "@/pages/EstimateForm";
import InvoiceView from "@/pages/InvoiceView";
import ConsolidatedInvoices from "@/pages/ConsolidatedInvoices";
import MonthlyConsumption from "@/pages/MonthlyConsumption";
import RoleManagement from "@/pages/RoleManagement";
import UserManagement from "@/pages/UserManagement";
import MasterData from "@/pages/MasterData";

function LoadingComponent() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-[#A80097] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-zinc-400 text-sm">Signing in...</p>
      </div>
    </div>
  );
}

function ErrorComponent({ error }) {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center max-w-md px-6">
        <p className="text-red-400 text-lg font-semibold mb-2">Authentication Failed</p>
        <p className="text-zinc-500 text-sm">{error?.message || "An error occurred during sign-in."}</p>
      </div>
    </div>
  );
}

function AppContent() {
  const { accounts } = useMsal();
  const { setUserPermissions, setUserRoles, setCurrentUserId, setPermissionsLoaded } = usePermissions();
  const permissionsFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch permissions once per account
    if (permissionsFetchedRef.current) {
      return;
    }

    if (accounts && accounts.length > 0) {
      permissionsFetchedRef.current = true;
      
      console.log("AppContent: Verifying current user from token");
      
      // Verify current user from token - backend extracts email and checks database
      usersService.verifyProfile()
        .then((response) => {
          const userData = response.data;
          console.log("AppContent: User profile verified successfully:", {
            email: userData.email,
            in_database: userData.in_database,
            user_id: userData.user_id,
            roles: userData.roles?.length || 0,
            pages: userData.pages?.length || 0
          });
          
          if (userData.in_database && userData.user_id) {
            // User is in database - set permissions directly from verify response
            setCurrentUserId(userData.user_id);
            setUserRoles(userData.roles || []);
            // Convert pages to permissions format
            setUserPermissions(userData.pages || []);
            setPermissionsLoaded(true);
            console.log("AppContent: User permissions loaded from token verification");
          } else {
            console.warn("AppContent: User email verified from token but not in database:", userData.email);
            // User not found in database - set empty permissions (will show AccessDenied)
            setCurrentUserId(null);
            setUserRoles([]);
            setUserPermissions([]);
            setPermissionsLoaded(true);
          }
        })
        .catch((err) => {
          console.error("AppContent: Error verifying user profile:", err);
          // On error, set empty permissions
          setCurrentUserId(null);
          setUserRoles([]);
          setUserPermissions([]);
          setPermissionsLoaded(true);
        });
    }
  }, [accounts, setUserPermissions, setUserRoles, setCurrentUserId, setPermissionsLoaded]);

  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<ProtectedRoute element={<Dashboard />} requiredPage="Dashboard" />} />
          <Route path="/consumption" element={<ProtectedRoute element={<MonthlyConsumption />} requiredPage="MonthlyConsumption" />} />
          <Route path="/estimate/new" element={<ProtectedRoute element={<EstimateForm />} requiredPage="EstimateForm" />} />
          <Route path="/estimate/:id" element={<ProtectedRoute element={<EstimateForm />} requiredPage="EstimateForm" />} />
          <Route path="/invoices" element={<ProtectedRoute element={<ConsolidatedInvoices />} requiredPage="ConsolidatedInvoices" />} />
          <Route path="/invoices/:id" element={<ProtectedRoute element={<InvoiceView />} requiredPage="InvoiceView" />} />
          <Route path="/admin/roles" element={<ProtectedRoute element={<RoleManagement />} requiredPage="RoleManagement" allowAdmin={true} />} />
          <Route path="/admin/users" element={<ProtectedRoute element={<UserManagement />} requiredPage="UserManagement" allowAdmin={true} />} />
          <Route path="/admin/master-data" element={<ProtectedRoute element={<MasterData />} requiredPage="MasterData" allowAdmin={true} />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

function App() {
  return (
    <div className="App">
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
        loadingComponent={LoadingComponent}
        errorComponent={ErrorComponent}
      >
        <InstallPromptProvider>
          <AppContent />
        </InstallPromptProvider>
      </MsalAuthenticationTemplate>
    </div>
  );
}

export default App;
